/**
 * Load Fancybox the first time someone actually opens a lightbox.
 *
 * Measured with Chrome's CSS/JS coverage on the live pages: vendor-fancybox.js is 40.2 KB
 * brotli and gigli-vendor-fancybox.css is 22 KB raw, and BOTH sit at 0% used on index and on
 * product. Nothing touches them until a click, because all the theme ever did with the library
 * was call Fancybox.bind() to register delegated handlers - and a delegated handler can just as
 * well be registered after the first click, as long as that click is replayed.
 *
 * That is what this does. It listens for the first click on a [data-fancybox] element, holds
 * the click, pulls in the stylesheet and the library, registers the same two binds that
 * gigli-bundle.js used to make, then re-dispatches the click so Fancybox handles it normally.
 * The user sees the lightbox open; the 62 KB never load for anyone who does not click.
 *
 * gigli-bundle.js guards both of its call sites with `typeof ye !== "undefined"`, so with the
 * library absent at load they no-op exactly as designed, and the binding moves here.
 */
(function () {
  "use strict";

  var CSS_URL = document.currentScript && document.currentScript.dataset.css;
  var JS_URL = document.currentScript && document.currentScript.dataset.js;
  if (!JS_URL) return;

  var state = "idle"; // idle -> loading -> ready

  function load(url, isCss) {
    return new Promise(function (resolve, reject) {
      var el;
      if (isCss) {
        el = document.createElement("link");
        el.rel = "stylesheet";
        el.href = url;
      } else {
        el = document.createElement("script");
        el.src = url;
      }
      el.onload = resolve;
      el.onerror = reject;
      document.head.appendChild(el);
    });
  }

  /**
   * vendor-fancybox.js declares `class ye` at top level. A top-level class binding lives in the
   * global LEXICAL scope, so later classic scripts see the bare identifier `ye` but nothing ever
   * appears on `window` - window.Fancybox is undefined even after the library has loaded. That
   * is exactly how gigli-bundle.js already reaches it, and why this has to resolve `ye` rather
   * than a window property. Identifier lookup happens when this runs, not when it was defined,
   * so being declared before the library loads is fine.
   */
  function getFancybox() {
    if (window.Fancybox) return window.Fancybox;
    try {
      return typeof ye !== "undefined" ? ye : null;
    } catch (e) {
      return null;
    }
  }

  function bind() {
    var F = getFancybox();
    if (!F) return false;
    // The same two binds gigli-bundle.js made. The bare bind() covers every [data-fancybox] and
    // groups by the attribute's value, so the reviews gallery keeps its own group.
    try {
      F.bind('[data-fancybox="reviews-gallery"]');
    } catch (e) {}
    try {
      F.bind();
    } catch (e) {}
    return true;
  }

  document.addEventListener(
    "click",
    function (event) {
      if (state === "ready") return; // Fancybox owns the click from here on
      var trigger = event.target.closest && event.target.closest("[data-fancybox]");
      if (!trigger) return;
      if (state === "loading") {
        // still fetching - swallow the click rather than following the href
        event.preventDefault();
        return;
      }

      state = "loading";
      event.preventDefault();
      event.stopPropagation();

      var work = CSS_URL ? [load(CSS_URL, true), load(JS_URL, false)] : [load(JS_URL, false)];
      Promise.all(work)
        .then(function () {
          state = "ready";
          if (!bind()) return;
          // Replay the click now that a delegated handler exists for it.
          trigger.dispatchEvent(new MouseEvent("click", { bubbles: true, cancelable: true }));
        })
        .catch(function () {
          // Library unreachable: fall back to the plain link so the image still opens.
          state = "idle";
          if (trigger.href) window.location.href = trigger.href;
        });
    },
    true // capture, so we get the click before any handler that might cancel it
  );
})();
